import type { TradePolicyAction, TradePolicyGroupOp } from "../types/tradePolicy";
import type { PickQuickStats } from "./reportPickQuickStats";
import { UNSCOPED_PICK_BUCKET } from "./reportPickListStorage";

const SIMULATOR_PICK_BUNDLES_STORAGE_KEY = "simulator_pick_bundles_v1";
const SIMULATOR_PICK_BUNDLES_CHANGED_EVENT = "simulatorPickBundlesChanged";

export type SimulatorPickBundleStatus = "running" | "succeeded" | "failed";

export type SimulatorPickBundleCard = {
  id: string;
  createdAtMs: number;
  /**
   * Report bucket id so picks + bundle cards stay scoped in report context.
   * For standalone/unscoped we use `UNSCOPED_PICK_BUCKET`.
   */
  reportBucketId: string;
  /** Run title shown on the Combined ran card (from the combine/run title field). */
  title?: string;
  /**
   * Snapshot of child pick labels at run time (joined with " + ").
   * Used when leaf picks are later removed from the board.
   */
  policySummary?: string;
  /**
   * Immediate atomic children:
   * - individual pick ids (leaf)
   * - other combined bundle card ids (atomic child)
   */
  children: string[];
  combineOp: TradePolicyGroupOp;
  /** Action used when simulating this bundle (veto/keep_only). */
  action?: TradePolicyAction;
  status: SimulatorPickBundleStatus;
  comparisonRunId?: string;
  jobId?: string;
  /** Derived from the bundle run simulation when the job finishes. */
  metrics?: PickQuickStats | null;
  /** How many larger combined bundles used this card as an atomic child. */
  used_as_child_count: number;
};

type PickBundlesStoreV1 = {
  v: 1;
  byBucket: Record<string, SimulatorPickBundleCard[]>;
};

function safeJsonParse(raw: string | null): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function bundleBucketId(reportId: string | null | undefined): string {
  const trimmed = reportId?.trim();
  return trimmed ? trimmed : UNSCOPED_PICK_BUCKET;
}

function loadStore(): PickBundlesStoreV1 {
  const parsed = safeJsonParse(localStorage.getItem(SIMULATOR_PICK_BUNDLES_STORAGE_KEY));
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return { v: 1, byBucket: {} };
  const o = parsed as Record<string, unknown>;
  if (o.v !== 1) return { v: 1, byBucket: {} };
  const byBucketRaw = (o.byBucket ?? {}) as unknown;
  if (!byBucketRaw || typeof byBucketRaw !== "object" || Array.isArray(byBucketRaw)) return { v: 1, byBucket: {} };
  return { v: 1, byBucket: byBucketRaw as Record<string, SimulatorPickBundleCard[]> };
}

function writeStore(store: PickBundlesStoreV1): void {
  localStorage.setItem(SIMULATOR_PICK_BUNDLES_STORAGE_KEY, JSON.stringify(store));
  window.dispatchEvent(new CustomEvent(SIMULATOR_PICK_BUNDLES_CHANGED_EVENT));
}

function stableSortNewestFirst(list: SimulatorPickBundleCard[]): SimulatorPickBundleCard[] {
  return [...list].sort((a, b) => b.createdAtMs - a.createdAtMs);
}

export function readBundlesForBucket(reportBucketId: string): SimulatorPickBundleCard[] {
  const store = loadStore();
  return stableSortNewestFirst(store.byBucket[reportBucketId] ?? []);
}

export function readAllBundlesFlat(): SimulatorPickBundleCard[] {
  const store = loadStore();
  const out: SimulatorPickBundleCard[] = [];
  for (const list of Object.values(store.byBucket)) out.push(...list);
  return stableSortNewestFirst(out);
}

export function getBundleCardById(bundleId: string, bundles?: SimulatorPickBundleCard[]): SimulatorPickBundleCard | null {
  if (bundles) {
    return bundles.find((b) => b.id === bundleId) ?? null;
  }
  return readAllBundlesFlat().find((b) => b.id === bundleId) ?? null;
}

export function addBundleCard(bundle: SimulatorPickBundleCard): void {
  const store = loadStore();
  const list = store.byBucket[bundle.reportBucketId] ? [...store.byBucket[bundle.reportBucketId]] : [];
  // Keep atomic ids unique within a bucket.
  const filtered = list.filter((b) => b.id !== bundle.id);
  filtered.unshift(bundle);
  store.byBucket[bundle.reportBucketId] = filtered;
  writeStore(store);
}

export function updateBundleCard(
  reportBucketId: string,
  bundleId: string,
  patch: Partial<Omit<SimulatorPickBundleCard, "id" | "reportBucketId" | "createdAtMs" | "children" | "combineOp">>
): void {
  const store = loadStore();
  const list = store.byBucket[reportBucketId];
  if (!list?.length) return;
  const idx = list.findIndex((b) => b.id === bundleId);
  if (idx < 0) return;

  const prev = list[idx]!;
  const next: SimulatorPickBundleCard = { ...prev, ...patch, used_as_child_count: prev.used_as_child_count };
  // Allow patching `used_as_child_count` explicitly.
  if (typeof patch.used_as_child_count === "number") next.used_as_child_count = patch.used_as_child_count;

  const updated = [...list];
  updated[idx] = next;
  store.byBucket[reportBucketId] = updated;
  writeStore(store);
}

export function incrementBundleUsedAsChildCount(
  reportBucketId: string,
  bundleId: string,
  delta: number
): void {
  const store = loadStore();
  const list = store.byBucket[reportBucketId];
  if (!list?.length) return;
  const idx = list.findIndex((b) => b.id === bundleId);
  if (idx < 0) return;
  const prev = list[idx]!;
  const nextCount = Math.max(
    0,
    (Number.isFinite(prev.used_as_child_count) ? prev.used_as_child_count : 0) + delta
  );
  const updated = [...list];
  updated[idx] = { ...prev, used_as_child_count: nextCount };
  store.byBucket[reportBucketId] = updated;
  writeStore(store);
}

export function removeBundleCard(reportBucketId: string, bundleId: string): void {
  const store = loadStore();
  const list = store.byBucket[reportBucketId];
  if (!list?.length) return;
  const next = list.filter((b) => b.id !== bundleId);
  if (next.length === list.length) return;
  if (next.length === 0) delete store.byBucket[reportBucketId];
  else store.byBucket[reportBucketId] = next;
  writeStore(store);
}

export function clearBundlesForBucket(reportBucketId: string): void {
  const store = loadStore();
  if (store.byBucket[reportBucketId]) {
    delete store.byBucket[reportBucketId];
    writeStore(store);
  }
}

export function clearAllBundles(): void {
  writeStore({ v: 1, byBucket: {} });
}

export function onSimulatorPickBundlesChanged(cb: () => void): () => void {
  window.addEventListener(SIMULATOR_PICK_BUNDLES_CHANGED_EVENT, cb as EventListener);
  return () => window.removeEventListener(SIMULATOR_PICK_BUNDLES_CHANGED_EVENT, cb as EventListener);
}

